import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

let searchInstanceCount = 0;

/**
 * A custom element that searches the storefront and displays predictive results.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement} [searchStatus]
 * @property {HTMLElement[]} [resultsItems]
 * @property {HTMLElement} [recentlyViewedWrapper]
 * @property {HTMLElement[]} [recentlyViewedTitle]
 * @property {HTMLElement[]} [recentlyViewedItems]
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  /** @type {AbortController | null} */
  #activeFetch = null;
  /** @type {AbortController | null} */
  #viewportController = null;
  #instanceId = `predictive-search-${++searchInstanceCount}`;
  #defaultMarkup = '';
  #emptyStateLoaded = false;
  #hasQueryResults = false;

  /** @returns {DialogComponent | null} */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    // morph() copies a root element's children, so retain a wrapper around the browse dropdown.
    this.#defaultMarkup ||= `<div>${this.refs.predictiveSearchResults.innerHTML}</div>`;
    const { dialog } = this;
    const { signal } = this.#controller;

    this.refs.resetButton.hidden = !this.refs.searchInput.value.length;
    this.#syncResultAccessibility();

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    } else if (!this.refs.searchInput.value.trim()) {
      void this.#loadEmptyState();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#viewportController?.abort();
    this.#cancelPending();
  }

  /** @param {KeyboardEvent} event */
  #handleKeyboardShortcut = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#viewportController?.abort();
    this.#resetSearch(false);
  };

  #handleDialogOpen = () => {
    this.#viewportController?.abort();
    this.#viewportController = new AbortController();
    const { signal } = this.#viewportController;
    window.visualViewport?.addEventListener('resize', this.#updateViewport, { signal });
    window.visualViewport?.addEventListener('scroll', this.#updateViewport, { signal });
    window.addEventListener('resize', this.#updateViewport, { signal });
    this.#updateViewport();
    this.#syncResultAccessibility();
    if (!this.refs.searchInput.value.trim()) void this.#loadEmptyState();
  };

  #updateViewport = () => {
    const dialog = this.dialog?.refs.dialog;
    if (!dialog?.open) return;
    const viewport = window.visualViewport;
    dialog.style.setProperty('--ns-search-height', `${viewport?.height ?? window.innerHeight}px`);
    dialog.style.setProperty('--ns-search-width', `${viewport?.width ?? window.innerWidth}px`);
    dialog.style.setProperty('--ns-search-top', `${viewport?.offsetTop ?? 0}px`);
    dialog.style.setProperty('--ns-search-left', `${viewport?.offsetLeft ?? 0}px`);
  };

  get #allResultsItems() {
    const seenLinks = new Set();
    return Array.from(
      this.refs.predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card')
    ).filter((item) => {
      const link = item.querySelector('a[href]');
      if (!(item instanceof HTMLElement) || !link || seenLinks.has(link)) return false;
      seenLinks.add(link);
      return true;
    });
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    const activeItem = items[index];
    for (const [itemIndex, item] of items.entries()) {
      item.classList.toggle('keyboard-focus', itemIndex === index);
      if (this.#hasQueryResults) item.setAttribute('aria-selected', String(itemIndex === index));
      else item.removeAttribute('aria-selected');
    }
    if (activeItem && this.#hasQueryResults) {
      this.refs.searchInput.setAttribute('aria-activedescendant', activeItem.id);
      activeItem.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    } else {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }
  }

  #syncResultAccessibility() {
    const { searchInput, predictiveSearchResults } = this.refs;
    const dropdown = predictiveSearchResults.querySelector('.predictive-search-dropdown');
    const items = this.#allResultsItems;
    const expanded = this.#hasQueryResults && items.length > 0 && (!this.dialog || this.dialog.refs.dialog.open);
    if (dropdown) {
      dropdown.id = `${this.#instanceId}-results`;
      searchInput.setAttribute('aria-controls', dropdown.id);
      searchInput.removeAttribute('aria-owns');
      dropdown.removeAttribute('aria-expanded');
      if (this.#hasQueryResults && items.length > 0) dropdown.setAttribute('role', 'listbox');
      else dropdown.removeAttribute('role');
    }
    for (const [index, item] of items.entries()) {
      if (this.#hasQueryResults) {
        item.id = `${this.#instanceId}-option-${index}`;
        item.setAttribute('role', 'option');
      } else {
        item.removeAttribute('role');
      }
    }
    searchInput.setAttribute('aria-expanded', String(expanded));
    this.#currentIndex = -1;
  }

  /** Keep normal Tab traversal and native form submission; arrow keys select suggestions only in the input.
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    const target = event.composedPath?.()[0] ?? event.target;
    if (target !== this.refs.searchInput || event.isComposing) return;
    if (event.key === 'Tab') {
      this.#currentIndex = -1;
      return;
    }
    if (event.key === 'Escape') {
      if (!this.dialog) this.resetSearch(false);
      return;
    }
    if (!this.#hasQueryResults || !this.#allResultsItems.length) return;

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.#currentIndex = event.key === 'ArrowDown'
        ? (currentIndex + 1) % totalItems
        : (currentIndex > 0 ? currentIndex - 1 : totalItems - 1);
    } else if (event.key === 'Enter' && currentIndex >= 0) {
      event.preventDefault();
      this.#allResultsItems[currentIndex]?.querySelector('a')?.click();
    }
  };

  /** @param {MouseEvent} event */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();
    RecentlyViewed.clearProducts();
    this.#emptyStateLoaded = false;
    if (!this.refs.searchInput.value.trim()) {
      this.#resetSearch(false);
      // The Clear button disappears. Restore keyboard focus without opening the keyboard after a touch.
      if (event.detail === 0) this.refs.searchInput.focus({ preventScroll: true });
    } else {
      this.refs.recentlyViewedWrapper?.remove();
      this.#syncResultAccessibility();
    }
  }

  /** @param {boolean | Event} [keepFocus=true] */
  resetSearch = (keepFocus = true) => {
    this.#resetSearch(true);
    if (keepFocus !== false) this.refs.searchInput.focus({ preventScroll: true });
  };

  /** Input invalidates stale work immediately, before the next request is debounced.
   * @param {InputEvent} event
   */
  search = (event) => {
    this.#cancelPending();
    this.#hasQueryResults = false;
    this.#syncResultAccessibility();
    this.#setStatus('');
    const searchTerm = this.refs.searchInput.value.trim();
    this.refs.resetButton.hidden = !this.refs.searchInput.value.length;
    if (!searchTerm) {
      this.#resetSearch(true);
      return;
    }
    if (event.isComposing) return;
    this.#debouncedSearch(searchTerm);
  };

  #debouncedSearch = debounce((searchTerm) => {
    if (this.refs.searchInput.value.trim() === searchTerm) void this.#getSearchResults(searchTerm);
  }, 200);

  #cancelPending() {
    this.#debouncedSearch.cancel();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /** @param {'' | 'loading' | 'error'} state */
  #setStatus(state) {
    const { searchStatus, predictiveSearchResults } = this.refs;
    predictiveSearchResults.setAttribute('aria-busy', String(state === 'loading'));
    if (!searchStatus) return;
    searchStatus.hidden = !state;
    searchStatus.textContent = state === 'loading'
      ? (searchStatus.dataset.loadingCopy || 'Searching…')
      : state === 'error' ? (searchStatus.dataset.errorCopy || 'Search previews are unavailable. Press Search to see all results.') : '';
  }

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /** @param {string} searchTerm */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    this.#setStatus('loading');
    const deferredPromise = SearchUpdateEvent.createPromise();
    // Listeners may consume this promise; a preview error must also be safe without analytics listeners.
    void deferredPromise.promise.catch(() => {});
    this.dispatchEvent(new SearchUpdateEvent({ search: { query: searchTerm }, promise: deferredPromise.promise }));

    try {
      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, abortController.signal);
      if (abortController.signal.aborted || this.refs.searchInput.value.trim() !== searchTerm) {
        deferredPromise.reject(new Error('Fetch aborted by user'));
        return;
      }
      if (!markup || !markup.includes('predictive-search-dropdown')) throw new Error('Search results unavailable');
      morph(this.refs.predictiveSearchResults, markup);
      this.#hasQueryResults = true;
      this.#syncResultAccessibility();
      this.#setStatus('');
      this.#resetScrollPositions();
      deferredPromise.resolve({ totalCount: this.#allResultsItems.length });
    } catch (error) {
      deferredPromise.reject(error);
      if (!abortController.signal.aborted) this.#setStatus('error');
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  /** Restore browse content immediately; recent products can arrive afterward without clearing typed text. */
  #resetSearch(loadRecent) {
    this.#cancelPending();
    this.refs.searchInput.value = '';
    this.refs.resetButton.hidden = true;
    this.#hasQueryResults = false;
    this.#emptyStateLoaded = false;
    this.#setStatus('');
    morph(this.refs.predictiveSearchResults, this.#defaultMarkup);
    this.#syncResultAccessibility();
    this.#resetScrollPositions();
    if (loadRecent) void this.#loadEmptyState();
  }

  async #loadEmptyState() {
    if (this.#emptyStateLoaded || this.refs.searchInput.value.trim() || !this.dataset.sectionId) return;
    const viewedProducts = RecentlyViewed.getProducts();
    if (!viewedProducts.length) return;
    this.#emptyStateLoaded = true;
    const abortController = new AbortController();
    this.#activeFetch?.abort();
    this.#activeFetch = abortController;
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, abortController.signal);
      if (abortController.signal.aborted || this.refs.searchInput.value.trim()) return;
      const parsedRecent = new DOMParser().parseFromString(markup, 'text/html');
      const recent = parsedRecent.querySelector('.recently-viewed-wrapper');
      const parsedDefault = new DOMParser().parseFromString(this.#defaultMarkup, 'text/html');
      const target = parsedDefault.querySelector('#predictive-search-products')
        || parsedDefault.querySelector('.predictive-search-results__inner');
      if (!recent || !target) return;
      target.prepend(recent);
      morph(this.refs.predictiveSearchResults, parsedDefault.body.innerHTML);
      this.#syncResultAccessibility();
    } catch {
      // Browse links and default products remain available if recent history cannot load.
      this.#emptyStateLoaded = false;
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
